import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, of, Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { InventoryItem } from '../../../services/tpl3pl.service';
import { Tpl3plInventoryUiService, InventorySortColumn } from '../../../services/tpl3pl-inventory-ui.service';
import { Tpl3plService } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { PrintService } from '../../../services/print.service';

@Component({
	selector: 'app-tpl3pl-inventory',
	templateUrl: './tpl3pl-inventory.component.html',
	styleUrls: ['./tpl3pl-inventory.component.scss'],
})
export class Tpl3plInventoryComponent implements OnInit, OnDestroy {
	inventoryStatus: any = null;
	inventoryItems: InventoryItem[] = [];
	itemListSearch = '';
	inventoryListTotal = 0;
	inventoryListLoading = false;
	inventoryPageIndex = 0;
	inventoryPageSize = 100;
	readonly inventoryPageSizeOptions = [50, 100, 250, 500, 1000];
	inventoryStatusFilter: '' | 'synced' | 'pending' | 'error' = '';
	inventorySortColumn: InventorySortColumn = 'last_sync';
	inventorySortDirection: 'asc' | 'desc' = 'desc';
	loading = false;
	selectedWarehouse = '';
	manualSyncRawLoading = false;
	clearing = false;
	exportingXls = false;

	// Unified inventory sync activity log.
	activityRows: any[] = [];
	activityTotal = 0;
	activityLoading = false;
	activityError: string | null = null;
	activityFilterStatus: '' | 'success' | 'error' | 'warning' = 'error';
	activityFilterFromDate: string = '';
	activityFilterToDate: string = '';
	activityFilterPo: string = '';
	activityLimit = 100;

	private get thirtyDaysAgo(): string {
		const d = new Date();
		d.setDate(d.getDate() - 30);
		return d.toISOString().slice(0, 10);
	}

	loadActivity(): void {
		this.activityLoading = true;
		this.activityError = null;
		const filters: any = {
			log_type: 'inventory_sync',
			limit: this.activityLimit,
		};
		if (this.activityFilterStatus) { filters.status = this.activityFilterStatus; }
		if (this.activityFilterFromDate) { filters.from_date = this.activityFilterFromDate; }
		if (this.activityFilterToDate) { filters.to_date = this.activityFilterToDate; }
		if ((this.activityFilterPo || '').trim()) { filters.orderno = this.activityFilterPo.trim(); }
		this.tpl3plService.getLogs(filters).subscribe({
			next: (res: any) => {
				this.activityLoading = false;
				this.activityRows = res.data || res || [];
				this.activityTotal = res.total || this.activityRows.length;
			},
			error: () => {
				this.activityLoading = false;
				this.activityError = 'Failed to load inventory sync activity';
			}
		});
	}

	resetActivityFilters(): void {
		this.activityFilterStatus = 'error';
		this.activityFilterFromDate = this.thirtyDaysAgo;
		this.activityFilterToDate = new Date().toISOString().slice(0, 10);
		this.activityFilterPo = '';
		this.loadActivity();
	}

	private configSavedSub: Subscription | null = null;
	private searchSub: Subscription | null = null;
	private search$ = new Subject<string>();

	constructor(
		private tpl3plService: Tpl3plService,
		private invUi: Tpl3plInventoryUiService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService
	) {}

	ngOnInit(): void {
		this.loadStatus();
		this.loadInventoryList();
		this.activityFilterFromDate = this.thirtyDaysAgo;
		this.activityFilterToDate = new Date().toISOString().slice(0, 10);
		this.loadActivity();
		this.configSavedSub = this.tpl3plService.configSaved$.subscribe(() => {
			this.loadStatus();
			this.inventoryPageIndex = 0;
			this.loadInventoryList();
		});
		this.searchSub = this.search$.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
			this.inventoryPageIndex = 0;
			this.loadInventoryList();
		});
	}

	ngOnDestroy(): void {
		this.configSavedSub?.unsubscribe();
		this.searchSub?.unsubscribe();
	}

	onInventorySearchInput(value: string): void {
		this.itemListSearch = value ?? '';
		this.search$.next((value || '').trim());
	}

	onRefresh(): void {
		this.inventoryPageIndex = 0;
		this.loadStatus();
		this.loadInventoryList();
	}

	private warehouseParam(): string | undefined {
		return this.invUi.warehouseParam(this.selectedWarehouse);
	}

	private listQuery(cacheBust?: boolean) {
		return {
			warehouse: this.warehouseParam(),
			statusFilter: this.inventoryStatusFilter || undefined,
			search: this.itemListSearch,
			sort: this.inventorySortColumn,
			order: this.inventorySortDirection,
			pageIndex: this.inventoryPageIndex,
			pageSize: this.inventoryPageSize,
			cacheBust,
		};
	}

	openManualSyncRaw(): void {
		this.invUi.openInventorySyncRawDialog(this.warehouseParam(), () => {
			this.loadStatus();
			this.inventoryPageIndex = 0;
			this.loadInventoryList();
		}, (v) => {
			this.manualSyncRawLoading = v;
		});
	}

	loadStatus(): void {
		this.loading = true;
		this.invUi.getInventoryStatus$(this.warehouseParam()).subscribe({
			next: (result) => {
				this.inventoryStatus = result;
				this.loading = false;
			},
			error: () => {
				this.globalSearchService.showNotification('Error loading inventory status', 'danger', 'bottom', 'right');
				this.loading = false;
			},
		});
	}

	loadInventoryList(): void {
		this.inventoryListLoading = true;
		this.invUi.getInventoryPage$(this.listQuery()).subscribe({
			next: ({ data, total }) => {
				const maxIndex = total <= 0 ? 0 : Math.max(0, Math.ceil(total / this.inventoryPageSize) - 1);
				if (this.inventoryPageIndex > maxIndex) {
					this.inventoryPageIndex = maxIndex;
					this.loadInventoryList();
					return;
				}
				this.inventoryItems = data;
				this.inventoryListTotal = total;
				this.inventoryListLoading = false;
			},
			error: () => {
				this.globalSearchService.showNotification('Error loading items at 3PL', 'danger', 'bottom', 'right');
				this.inventoryListLoading = false;
			},
		});
	}

	onInventoryPage(event: { pageIndex: number; pageSize: number }): void {
		this.inventoryPageIndex = event.pageIndex;
		this.inventoryPageSize = event.pageSize;
		this.loadInventoryList();
	}

	clearSyncedInventory(): void {
		if (
			!confirm(
				'Clear all synced 3PL inventory data and zero the corresponding ERP stock quantities? Use this after the 3PL has cleared their side so you can reload from scratch (Manual sync raw or scheduled job). This cannot be undone.'
			)
		) {
			return;
		}
		this.clearing = true;
		this.tpl3plService.clearInventorySync().subscribe({
			next: (result) => {
				this.clearing = false;
				if (result.success) {
					this.inventoryStatus = {
						...(this.inventoryStatus || {}),
						total_items: 0,
						synced_items: 0,
						pending_items: 0,
						error_items: 0,
						total_qoh: 0,
						total_available: 0,
						total_internal_qoh: 0,
						total_on_demand: 0,
						last_sync_time: null,
						last_sync: null,
						success: true,
					};
					this.inventoryItems = [];
					this.inventoryListTotal = 0;
					this.inventoryPageIndex = 0;
					this.globalSearchService.showNotification(result.message || 'Cleared. Reload cache via Manual sync (raw) or your scheduled sync job.', 'success', 'bottom', 'right');
					this.invUi.refreshStatusAndList$(this.listQuery(true)).subscribe({
						next: ({ status, data, total }) => {
							this.inventoryStatus = status;
							this.inventoryItems = data;
							this.inventoryListTotal = total;
						},
						error: () => {},
					});
				} else {
					this.globalSearchService.showNotification(result.message || 'Clear failed', 'danger', 'bottom', 'right');
				}
			},
			error: () => {
				this.clearing = false;
				this.globalSearchService.showNotification('Error clearing inventory sync data', 'danger', 'bottom', 'right');
			},
		}		);
	}

	setSort(column: InventorySortColumn): void {
		if (this.inventorySortColumn === column) {
			this.inventorySortDirection = this.inventorySortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			this.inventorySortColumn = column;
			this.inventorySortDirection = 'asc';
		}
		this.inventoryPageIndex = 0;
		this.loadInventoryList();
	}

	sortIcon(column: InventorySortColumn): string {
		if (this.inventorySortColumn !== column) {
			return 'unfold_more';
		}
		return this.inventorySortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	syncStatusBadgeClass(status: string): string {
		if (status === 'synced') {
			return 'badge-synced';
		}
		if (status === 'pending') {
			return 'badge-pending';
		}
		if (status === 'error') {
			return 'badge-error';
		}
		return 'badge-pending';
	}

	onStatusFilterChange(): void {
		this.inventoryPageIndex = 0;
		this.loadInventoryList();
	}

	get pageDisplayEnd(): number {
		return Math.min(this.inventoryPageIndex * this.inventoryPageSize + this.inventoryPageSize, this.inventoryListTotal);
	}

	onWarehouseBlur(): void {
		const raw = this.selectedWarehouse ?? '';
		const t = raw.trim();
		if (t !== raw) {
			this.selectedWarehouse = t;
			this.onRefresh();
		}
	}

	private static escHtml(v: unknown): string {
		return String(v ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	private exportBaseFilename(): string {
		const ts = new Date().toISOString().slice(0, 19).replace(/[T:-]/g, '');
		const wh = (this.selectedWarehouse || '').trim().replace(/[^a-zA-Z0-9_-]+/g, '_');
		return wh ? `tpl3pl_inventory_${ts}_${wh}` : `tpl3pl_inventory_${ts}`;
	}

	private formatLots(row: InventoryItem): string {
		if (!row.lots?.length) {
			return '';
		}
		return row.lots
			.map((l) => {
				const exp = l.expiration_date ? new Date(l.expiration_date).toLocaleDateString() : '—';
				return `${l.lot_number}:${l.quantity} exp ${exp} (${l.lot_status || 'available'})`;
			})
			.join('; ');
	}

	private loadAllInventoryRowsForExport(): Observable<InventoryItem[]> {
		const limit = 2000;
		const q = this.listQuery(true);
		const common = {
			warehouse: q.warehouse,
			status: q.statusFilter || undefined,
			search: (q.search || '').trim() || undefined,
			sort: q.sort,
			order: q.order,
			limit,
			cacheBust: true,
		};

		const fetchFrom = (offset: number): Observable<InventoryItem[]> => {
			return this.tpl3plService.getInventory({ ...common, offset }).pipe(
				switchMap((res) => {
					const rows = res.data || [];
					const total = res.total ?? 0;
					if (rows.length === 0 || offset + rows.length >= total) {
						return of(rows);
					}
					return fetchFrom(offset + rows.length).pipe(map((rest) => rows.concat(rest)));
				})
			);
		};

		return fetchFrom(0);
	}

	private buildInventoryExportHtml(rows: InventoryItem[]): string {
		const title = '3PL inventory (PGS)';
		const subtitle =
			'Exported ' +
			new Date().toLocaleString() +
			' · ' +
			rows.length +
			' row(s) · same filters as on screen';
		const th = [
			'SKU',
			'Warehouse',
			'PGS wh',
			'Internal QOH',
			'On demand',
			'3PL on hand',
			'3PL available',
			'Status',
			'Last sync',
			'Error message',
			'Lots',
		];
		const trs = rows
			.map((row) => {
				const lastSync = row.last_sync ? new Date(row.last_sync).toLocaleString() : '—';
				return (
					'<tr>' +
					`<td>${Tpl3plInventoryComponent.escHtml(row.stockid)}</td>` +
					`<td>${Tpl3plInventoryComponent.escHtml(row.warehouse_code)}</td>` +
					`<td>${Tpl3plInventoryComponent.escHtml(row.pgs_warehouse || '—')}</td>` +
					`<td>${Tpl3plInventoryComponent.escHtml(row.internal_qoh)}</td>` +
					`<td>${Tpl3plInventoryComponent.escHtml(row.on_demand)}</td>` +
					`<td>${Tpl3plInventoryComponent.escHtml(row.quantity_on_hand)}</td>` +
					`<td>${Tpl3plInventoryComponent.escHtml(row.quantity_available)}</td>` +
					`<td>${Tpl3plInventoryComponent.escHtml(row.sync_status)}</td>` +
					`<td>${Tpl3plInventoryComponent.escHtml(lastSync)}</td>` +
					`<td style="white-space:pre-wrap;max-width:360px;">${Tpl3plInventoryComponent.escHtml(row.error_message || '')}</td>` +
					`<td style="white-space:pre-wrap;max-width:420px;font-size:11px;">${Tpl3plInventoryComponent.escHtml(this.formatLots(row))}</td>` +
					'</tr>'
				);
			})
			.join('');
		return (
			'<div class="table">' +
			`<div style="font-size:18px;font-weight:bold;margin-bottom:4px;">${Tpl3plInventoryComponent.escHtml(title)}</div>` +
			`<div style="margin-bottom:12px;">${Tpl3plInventoryComponent.escHtml(subtitle)}</div>` +
			'<table class="table table-striped table-sm table-bordered" style="border-collapse:collapse;width:100%;">' +
			'<thead><tr>' +
			th.map((h) => `<th style="text-align:left;padding:6px;border:1px solid #ccc;">${Tpl3plInventoryComponent.escHtml(h)}</th>`).join('') +
			'</tr></thead><tbody>' +
			trs +
			'</tbody></table></div>'
		);
	}

	exportXls(): void {
		if (this.inventoryListTotal === 0) {
			this.globalSearchService.showNotification('No rows to export', 'warning', 'bottom', 'right');
			return;
		}
		this.exportingXls = true;
		this.loadAllInventoryRowsForExport().subscribe({
			next: (rows) => {
				if (!rows.length) {
					this.exportingXls = false;
					this.globalSearchService.showNotification('No rows to export', 'warning', 'bottom', 'right');
					return;
				}
				const html = this.buildInventoryExportHtml(rows);
				const filename = this.exportBaseFilename();
				const today = new Date();
				const data = {
					content: this.globalSearchService.base64encode(html),
					filename,
					title: '3PL inventory',
					subtitle: 'Created ' + today.toLocaleString(),
				};
				this.printService.xls(data).subscribe({
					next: (result: any) => {
						this.exportingXls = false;
						if (result?.content) {
							this.globalSearchService.downloadXls(result.content, filename + '.xlsx');
						} else {
							this.globalSearchService.showNotification('Excel export returned no file', 'warning', 'bottom', 'right');
						}
					},
					error: () => {
						this.exportingXls = false;
						this.globalSearchService.showNotification('Excel export failed', 'danger', 'bottom', 'right');
					},
				});
			},
			error: () => {
				this.exportingXls = false;
				this.globalSearchService.showNotification('Could not load inventory for export', 'danger', 'bottom', 'right');
			},
		});
	}
}
